import { useEffect, useState } from 'react';
import {
  useLocation,
  useParams,
  Navigate,
  Routes,
  Route,
  useNavigate,
} from 'react-router-dom';

import Header from '../../common/Header';
import { Tabs } from '../../common/Tabs';
import { Spinner } from '../../components/Spinner';

import { IndividualView } from './containers/IndividualView';
import { TeamView } from './containers/TeamView';
import { TeamProvider, useTeam } from './contexts/TeamContext';

function YourTeamsContent(): JSX.Element {
  const { team, isLoading, error } = useTeam();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    setSelectedTab(location.pathname.includes('individualView') ? 1 : 0);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        Error loading team: {error.message}
      </div>
    );
  }

  if (!team) {
    return <div className="text-center text-gray-600 p-4">Team not found</div>;
  }

  return (
    <div className="flex flex-col w-full min-h-screen max-w-screen-l mt-4 mx-auto">
      <Header>{team.name} Team</Header>
      <Tabs
        items={[
          { name: 'Team', value: `/your-teams/${team.id}/teamView` },
          {
            name: 'Individual',
            value: `/your-teams/${team.id}/individualView`,
          },
        ]}
        selectedIndex={selectedTab}
        onChange={(index, value) => {
          if (value) {
            navigate(value);
          }
          setSelectedTab(index);
        }}
      />

      <Routes>
        <Route path="teamView" element={<TeamView />} />
        <Route path="individualView" element={<IndividualView />} />
        <Route path="*" element={<Navigate to="teamView" replace />} />
      </Routes>
    </div>
  );
}

export function YourTeamsPage(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();

  if (!teamId) {
    return <Navigate to="/" replace />;
  }

  return (
    <TeamProvider teamId={teamId}>
      <YourTeamsContent />
    </TeamProvider>
  );
}
